import React from 'react';

import classNames from 'classnames';

import { Typography } from '../Typography';
import { Box } from '../Box';
import { StagesSize } from '../Stages';

import { StageEnd } from './StageEnd';
import { StageStart } from './StageStart';
import { useStyles } from './Stage.styles';

export type StageStatus = 'initial' | 'active' | 'completed';

interface StageProps {
  className?: string;
  first?: boolean;
  label: string;
  last?: boolean;
  size?: StagesSize;
  status?: StageStatus;
  fullWidth?: boolean;
}

/**
 * Компонент, представляющий этап в процессе этапов, который может находиться в статусе 'initial', 'active' или 'completed'.
 * Включает индикаторы начала и конца, метку и может быть стилизован на основе своей позиции и размера.
 *
 * @param {StageProps} props - Свойства компонента Stage.
 * @param {string} [props.className] - Дополнительное имя CSS-класса для применения к Stage.
 * @param {boolean} [props.first=false] - Определяет, является ли этот этап первым в процессе.
 * @param {string} props.label - Метка для отображения этого этапа.
 * @param {boolean} [props.last=false] - Определяет, является ли этот этап последним в процессе.
 * @param {StagesSize} [props.size='medium'] - Размер этапа, влияющий на его внешний вид.
 * @param {StageStatus} [props.status='initial'] - Статус этапа ('initial', 'active' или 'completed').
 * @param {boolean} [props.fullWidth=false] - Определяет, должен ли этап занимать всю ширину своего контейнера.
 * @returns {JSX.Element} - Отрендеренный компонент Stage.
 */

export const Stage = ({
  className,
  first = false,
  label,
  last = false,
  fullWidth = false,
  size = 'medium',
  status = 'initial',
}: StageProps) => {
  const classes = useStyles();

  const completed = status === 'completed';
  const active = status === 'active';

  const widthForContainer = fullWidth ? '100%' : 'auto';

  return (
    <Box
      display="flex"
      flexWrap="nowrap"
      width={widthForContainer}
      className={classNames(
        className,
        !first && classes.offsetToLeft,
        !first && size === 'small' && classes.offsetToLeftSmall,
        'aqa_stage',
        `aqa_stage_${status}`,
      )}
      testId="stage-test-id"
    >
      <Box display="flex" width={widthForContainer}>
        <StageStart first={first} status={status} />
        <Box
          className={classNames(
            classes.content,
            fullWidth && classes.fullWidthStageContent,
            completed && classes.completed,
            active && classes.active,
            size === 'medium' && classes.contentMedium,
          )}
        >
          <Typography
            variant="caption"
            color={
              completed || active ? 'text.dark.primary' : 'text.light.primary'
            }
          >
            {label}
          </Typography>
        </Box>
        <StageEnd last={last} status={status} />
      </Box>
    </Box>
  );
};
